import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem, DragDropModule } from '@angular/cdk/drag-drop';
import { v4 as uuidv4 } from 'uuid';
import { TemplateNodeEditorComponent } from './template-node-editor/template-node-editor.component';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TemplateDragStateService } from './template-drag-state.service';

export interface TemplateNode {
  id?: string;
  name: string;
  type: string; // 'string' | 'number' | 'object'
  children?: TemplateNode[];
  min?: number;
  max?: number;
  expanded?: boolean;
  value?: any;
}

@Component({
  selector: 'app-certification-template-editor',
  templateUrl: './certification-template-editor.component.html',
  styleUrls: ['./certification-template-editor.component.css'],
  standalone: true,
  imports: [DragDropModule, TemplateNodeEditorComponent, CommonModule, MatButtonModule, MatIconModule],
  providers: [TemplateDragStateService]
})
export class CertificationTemplateEditorComponent implements OnInit {
  @Input() shapeJson: string;
  @Output() shapeJsonChange = new EventEmitter<string>();

  nodes: TemplateNode[] = [];

  constructor(public dragState: TemplateDragStateService) { }

  ngOnInit() {
    if (this.shapeJson) {
      try {
        const parsed = JSON.parse(this.shapeJson.toString());
        this.nodes = Array.isArray(parsed) ? this.normalizeNodes(parsed) : [];
      } catch {
        this.nodes = [];
      }
    }
  }

  addNode() {
    this.nodes.push({
      id: uuidv4(),
      name: '',
      type: 'string',
      children: [],
      expanded: true
    });
    this.save();
  }

  onNodesChange() {
    this.save();
  }

  onRootTargetEnter(): void {
    this.dragState.enterRoot(this.nodes);
  }

  onRootTargetExit(): void {
    this.dragState.leaveRoot(this.nodes);
  }

  save() {
    const json = JSON.stringify(this.nodes);
    this.shapeJsonChange.emit(json);
  }

  drop(event: CdkDragDrop<TemplateNode[]>) {
    if (this.dragState.applyInsideDrop(event)) {
      this.save();
      return;
    }

    if (event.previousContainer.data === event.container.data) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
    this.dragState.reset();
    this.save();
  }

  private normalizeNodes(nodes: TemplateNode[]): TemplateNode[] {
    return nodes.map(node => ({
      ...node,
      id: node.id || uuidv4(),
      name: node.name || '',
      type: node.type || (node.children?.length ? 'object' : 'string'),
      children: Array.isArray(node.children) ? this.normalizeNodes(node.children) : [],
      expanded: node.expanded ?? true
    }));
  }
}
